import { Stack, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import HighlightedText from 'src/components/HighlightedText';
import LoadingRows from 'src/components/Table/LoadingRows';
import NoDataCell from 'src/components/NoDataCell';
import TableContainer from 'src/components/Table/TableContainer';
import TableHeadTypography from 'src/components/TableHeadTypography';
import { TableCellTypography } from 'src/components/GenericTableCell';
import useTypographyMediaQuery from 'src/hooks/useTypographyMediaQuery';
import { pagesText } from 'src/constants/pagesText';
import { isDateString } from 'src/utils/date.util';
import { formatDate } from 'src/utils/formatTime';

// Highlighting only understands non-empty strings and falls back to a dash for everything else, so
// counts have to be stringified here or they never render. Dates match what the generic row did,
// where a version string is deliberately not a date.
const displayValue = value => {
  if (isDateString(value)) {
    return formatDate(value);
  }

  return typeof value === 'number' ? value.toLocaleString() : value;
};

// Only a column that asks for one gets the flex row, so an aligned cell keeps its alignment.
const withAdornment = (valueNode, adornment) =>
  adornment ? (
    <Stack direction="row" gap={0.75} alignItems="center" flexWrap="wrap">
      {valueNode}
      {adornment}
    </Stack>
  ) : (
    valueNode
  );

// The shared shell for the list tabs of a details page: compact rows, one flat header, no row
// striping. Columns are { key, label, width, align?, wrap?, render?, adornment? }; render replaces
// the cell outright, while adornment keeps the highlighted value and puts a node beside it.
const CompactDataTable = ({
  columns,
  rows,
  isLoading,
  isFetching,
  lastElementRef,
  onRowClick,
  searchValue = '',
}) => {
  const { mediumScreen, largeScreen, smallScreen } = useTypographyMediaQuery();
  const showLoadingRows = isLoading || isFetching;

  return (
    <TableContainer roundedTop styles={{ maxHeight: `calc(100vh - 372px)` }}>
      <Table
        stickyHeader
        size="small"
        sx={{
          tableLayout: 'fixed',
          width: '100%',
          '& .MuiTableCell-root': { fontSize: '0.7rem' },
        }}>
        <colgroup>
          {columns.map(column => (
            <col key={column.key} style={{ width: column.width }} />
          ))}
        </colgroup>
        <TableHead sx={{ backgroundColor: '#3f3f3f', borderRadius: 0 }}>
          <TableRow>
            {columns.map(column => (
              <TableCell
                key={column.key}
                align={column.align}
                sx={{ top: 0, p: '8px 16px', verticalAlign: 'middle' }}>
                <TableHeadTypography
                  mediumScreen={mediumScreen}
                  largeScreen={largeScreen}
                  smallScreen={smallScreen}>
                  {column.label}
                </TableHeadTypography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody sx={{ backgroundColor: '#4B4B4B' }}>
          {!showLoadingRows && !rows?.length && (
            <NoDataCell colSpan={columns.length}>{pagesText.noDataFound}</NoDataCell>
          )}
          {(rows || []).map((row, index) => (
            <TableRow
              key={row.id ?? index}
              ref={index === rows.length - 1 ? lastElementRef : null}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              sx={{ '& td': { py: 0.35 }, cursor: onRowClick ? 'pointer' : 'default' }}>
              {columns.map(column => (
                <TableCell
                  key={column.key}
                  align={column.align}
                  sx={{
                    py: 0.5,
                    minWidth: 0,
                    ...(column.wrap ? { wordBreak: 'break-word' } : {}),
                    ...(column.capitalize ? { textTransform: 'capitalize' } : {}),
                  }}>
                  {column.render
                    ? column.render(row, searchValue)
                    : withAdornment(
                        <TableCellTypography sx={{ fontSize: '0.7rem', m: 0, display: 'block' }}>
                          <HighlightedText
                            text={displayValue(row[column.key])}
                            searchValue={searchValue}
                            fallback="—"
                          />
                        </TableCellTypography>,
                        column.adornment?.(row),
                      )}
                </TableCell>
              ))}
            </TableRow>
          ))}
          {showLoadingRows ? <LoadingRows columnCount={columns.length} rowCount={5} /> : null}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default CompactDataTable;
